import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  ViewChild,
  inject,
  OnDestroy,
  TemplateRef,
  PLATFORM_ID,
  Input,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  DomSanitizer,
  SafeHtml,
  SafeResourceUrl,
} from '@angular/platform-browser';

import { SmallHeaderComponent } from '../../partials/small-header/small-header.component';
import { TabComponent } from '../tab/tab.component';

import { MatIconModule } from '@angular/material/icon';

import { DestinationsService } from '../../../reducers/services/destinations.service';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { DualCalendarComponent } from '../dual-calender/dual-calender.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { RoomsComponent } from '../rooms/rooms.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { CdkNoDataRow } from '@angular/cdk/table';
import { MatMenuModule } from '@angular/material/menu';
import { ButtonComponent } from '../buttons/button/button.component';
import { MatButtonModule } from '@angular/material/button';

import { HotelSearchComponent } from '../../../components/hotel-search/hotel-search.component';
import { SafeUrl } from '@angular/platform-browser';
import lgVideo from 'lightgallery/plugins/video';
import { HostListener } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs/operators';
import { ApplicationRef, NgZone } from '@angular/core';
import { RoomService } from '../../../components/services/room.service';
import { HotelGalleryComponent } from './hotel-gallery';
import { FullPageLoaderComponent } from '../full-page-loader/full-page-loader.component';
import { ScrollToTopButtonComponent } from '../scroll-to-top/scroll-to-top.component';
import {
  RoomGuestMenuComponent,
  RoomGuest,
} from './room-guest-menu/room-guest-menu.component';
import { bookingService } from '../../../components/booking/booking.service';
import { CartStore } from '../../../components/booking/cart.store';
import { switchMap } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BookingStepOneComponent } from '../../../components/booking/booking-step-1/booking-step-one.component';
import { HotelRoomComponent } from '../hotel-room/hotel-room.component';
import { MetaTagsService } from '../../../reducers/services/meta-tags.service';
import { JsonLdService } from '../../../components/services/json-ld.service';

interface GalleryMediaItemApi {
  cdnUrl?: string;
  location?: string;
  fileType?: string;
  metadata?: {
    thumbnail?: string;
    poster?: string;
  };
}

interface GalleryCategoryApi {
  hotelTagName?: string;
  items?: GalleryMediaItemApi[];
}

interface GalleryTagGroup {
  tagId: string;
  label: string;
  count: number;
  coverUrl: string;
}

@Component({
  selector: 'app-hotel-description',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SmallHeaderComponent,
    TabComponent,
    ReactiveFormsModule,
    MatIconModule,
    DualCalendarComponent,
    RoomsComponent,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgSelectComponent,
    MatMenuModule,
    ButtonComponent,
    MatButtonModule,
    MatProgressSpinnerModule,
    HotelGalleryComponent,
    FullPageLoaderComponent,
    ScrollToTopButtonComponent,
    RoomGuestMenuComponent,
    BookingStepOneComponent,
    HotelRoomComponent,
  ],
  templateUrl: './hotel-description.component.html',
  styleUrls: ['./hotel-description.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HotelDescriptionComponent implements OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
    private appRef: ApplicationRef,
    private ngZone: NgZone,
    private destinationService: DestinationsService,
    private bookingService: RoomService,
    private cartApi: bookingService,
    public cartStore: CartStore,
    private metaTagsService : MetaTagsService,
    private jsonLd : JsonLdService,
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  private _data: any;

  @Input() set data(value: any) {
    if (!value) return;

    this._data = value;
    this.hotelData = value;
    this.hotelTitle = value?.name || '';
    this.hotelSlug = value?.slug || this.hotelSlug;
    this.setDescriptionHtml(value?.description);

    const raw = value?.mapLink || '';
    const embedUrl = this.toGoogleEmbedUrl(raw);
    this.safeMapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);

    this.attractions = Array.isArray(value?.attractions)
    ? value.attractions
    : [];

    this.applyMetaTagsFromHotel(value);
    this.setHotelDetailsJsonLd();
  }
  get data(): any {
    return this._data;
  }

  @ViewChild('dynamicGallery', { static: false })
  dynamicGalleryRef!: ElementRef<HTMLElement>;

  @ViewChild('autoPlayVideo')
  autoPlayVideo!: ElementRef<HTMLVideoElement>;

  isPreviewPlaying = true;

  showClearCartConfirm = false;

  // will store what to do after clearing cart
  private pendingAction: 'search' | 'availability' | null = null;

  // payload/state we need to continue after clearing
  private pendingSearchState: any = null;
  private pendingAvailabilityPayload: any = null;
  private pendingAvailabilityHotelSlug: string | null = null;
  pendingSearchDestinationSlug: string | null = null;
  pendingAvailabilityDestinationSlug: string | null = null;
  private readonly DATE_STATE_KEY = 'txl_hotel_dates_v1';
  private destroy$ = new Subject<void>();
  private lastHotelSlug = '';

  safeMapUrl: SafeResourceUrl | null = null;
  private preSelectedHotelId: string | null = null;
  private preSelectedDestinationId: string | null = null;
  private sheetRef?: MatBottomSheetRef;
  today = new Date();
  checkIn: Date | null = null;
  checkOut: Date | null = null;
  private lgInstance: any;

  searchDisabled = true;
  private initialDestinationId: string | null = null;

  private destinationsService = inject(DestinationsService);

  private applyBookingTries = 0;
  private readonly maxApplyBookingTries = 20;
  type: string = '';
  Math = Math;
  hotelSlug = '';
  hotelData: any;
  hotelImage!: string;
  hotelTitle!: string;
  hotelListOnSelectedDestination: any[] = [];
  hotelLocation!: string;
  isExpanded = false;
  roomsSelected = 1;
  adultsSelected = 2;
  childrenSelected = 0;
  infantsSelected = 0;
  bookingDataState: any = null;

  formErrorMessage: string | null = null;

  hasVideoFile = false;
  visibleImages: any[] = [];
  overflowCount = 0;
  baseIndex: number = 0;

  isMobile = false;

  roomMin = 1;
  roomMax = 5;
  adultMin = 1;
  adultMax = 8;
  childMin = 0;
  childMax = 6;
  infantMin = 0;
  infantMax = 6;

  amenities = [
    'Free WiFi',
    'Swimming Pool',
    'Spa & Wellness',
    'Restaurant',
    'Free Parking',
    'Fitness Center',
    'Bar',
    'Room Service',
  ];

  tabs = [
    { label: 'Overview', target: '#overview-section' },
    { label: 'About', target: '#about-section' },
    { label: 'Rooms', target: '#rooms-section' },
    // { label: 'Gallery', target: '#gallery-section' },
    // { label: 'Location', target: '#location-section' },
    // { label: 'FAQs', target: '#faqs-section' },
  ];

  rooms: any[] = [];
  hotelAmenities: { name: string }[] = [];
  attractions: any[] = [];
  encodedMapUrl: SafeResourceUrl = '';
  form!: FormGroup;
  ratingScore = 9.2;
  ratingLabel = 'Exceptional';
  totalReviews = 1201;
  ratingTags = [
    'Cleanliness 9.6',
    'Location 9.0',
    'Service 9.5',
    'Facilities 9.4',
  ];
  selectedHotel: string = '';
  page = 1;
  limit = 9;
  hotelList: any[] = [];
  isDropdownVisible = false;
  isAdultDropdownVisible = false;
  dynamicPlaceholder!: string;
  @ViewChild('bottomSheetTemplate') bottomSheetTemplate!: TemplateRef<any>;
  @ViewChild('adultBottomSheetTemplate')
  adultBottomSheetTemplate!: TemplateRef<any>;
  checkoutUrl: string | null = null;
  destinations: any[] = [];
  loading = false;
  dropdownPosition = {
    top: '0px',
    left: '0px',
    maxHeight: '0px',
    width: '0px',
    overflowY: 'hidden',
  };

  primaryVideoSafeUrl: SafeUrl | null = null;
  showVideoOverlay = false;
  primaryVideoUrl: string | null = null;
  primaryVideoPoster: string =
    'https://via.placeholder.com/1280x720?text=Video';

  galleryTagGroups: GalleryTagGroup[] = []; //
  galleryPreviewGroups: GalleryTagGroup[] = [];
  hasMoreGalleryGroups = false;
  moreGalleryGroupsCount = 0;
  isGalleryModalOpen = false;
  galleryModalTag: string | null = null;
  showAvailability = false;
  hasPendingBookingState = false;
  pendingBookingHotelId: string | null = null;
  pendingBookingDestinationId: string | null = null;

  private scrollListener: (() => void) | null = null;
  private currentDropdownTarget: HTMLElement | null = null;
  hotelDescriptionHtml = '';
  private readonly SITE_URL = 'https://tripxl.com';
  private readonly HOTELS_BASE = `${this.SITE_URL}/our-hotels`;

  @HostListener('document:keydown.escape', [])
  onEsc() {
    if (this.showVideoOverlay) this.closeInlineVideo();
  }

  private showFormError(message: string) {
    this.formErrorMessage = message;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.scrollToSearchForm();
    }, 0);
  }

  private platformId = inject(PLATFORM_ID);

  private pendingLoads = 0;
  private firstShownAt = 0;

  private setBodyScrollLock(lock: boolean) {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = lock ? 'hidden' : '';
    }
  }

  private flushNow() {
    this.ngZone.run(() => {
      try {
        this.cdr.detectChanges();
      } catch { }
      try {
        this.appRef.tick();
      } catch { }
    });
  }

  private beginLoad() {
    this.pendingLoads++;
    if (!this.loading) {
      this.loading = true;
      this.firstShownAt = performance.now();
      this.flushNow();
    }
  }

  private endLoad() {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
    if (this.pendingLoads === 0 && this.loading) {
      const elapsed = performance.now() - this.firstShownAt;
      const remain = Math.max(0, 200 - elapsed); // min 200ms
      setTimeout(() => {
        if (this.pendingLoads === 0) {
          this.loading = false;
          this.flushNow();
        }
      }, remain);
    }
  }

  private isSameDay(a: Date | null, b: Date | null): boolean {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private applyBookingData(bookingData: any): void {
    this.checkIn = bookingData?.checkInDate
      ? new Date(bookingData.checkInDate)
      : null;
    this.checkOut = bookingData?.checkOutDate
      ? new Date(bookingData.checkOutDate)
      : null;

    // ✅ Convert bookingData.rooms -> RoomGuest[] (with child ages support)
    const rawRooms: any[] = Array.isArray(bookingData?.rooms)
      ? bookingData.rooms
      : [];

    if (rawRooms.length) {
      this.roomGuests = rawRooms.map((room: any) => {
        const adults = Number(room?.adults ?? 2);

        // supports { child: 2 } OR { children: 2 } OR { children: [5,7] }
        const childrenCount = Array.isArray(room?.children)
          ? room.children.length
          : Number(room?.child ?? room?.children ?? 0);

        // supports { childAges: [...] } OR { children: [...] } (party-like)
        const incomingAges = Array.isArray(room?.childAges)
          ? room.childAges
          : Array.isArray(room?.children)
            ? room.children
            : [];

        const childAges = incomingAges.slice(0, childrenCount);
        while (childAges.length < childrenCount) childAges.push(null);

        return {
          id: room?.id ?? this.makeId(),
          adults: Number.isFinite(adults) && adults > 0 ? adults : 1,
          children:
            Number.isFinite(childrenCount) && childrenCount >= 0
              ? childrenCount
              : 0,
          childAges,
        };
      });
    } else {
      this.roomGuests = [
        { id: this.makeId(), adults: 2, children: 0, childAges: [] },
      ];
    }

    // ✅ Always derive totals from roomGuests (so UI stays in sync)
    this.roomsSelected = this.roomGuests.length || 1;
    this.adultsSelected = this.roomGuests.reduce(
      (s, r) => s + (r.adults ?? 0),
      0,
    );
    this.childrenSelected = this.roomGuests.reduce(
      (s, r) => s + (r.children ?? 0),
      0,
    );
    this.infantsSelected = Number(
      bookingData?.totalInfants ?? bookingData?.infants ?? 0,
    );

    this.dynamicPlaceholder =
      `${this.roomsSelected} Room${this.roomsSelected !== 1 ? 's' : ''} | ` +
      `${this.adultsSelected} Adult${this.adultsSelected !== 1 ? 's' : ''} | ` +
      `${this.childrenSelected} Child${this.childrenSelected !== 1 ? 'ren' : ''
      }` +
      (this.infantsSelected > 0
        ? ` | ${this.infantsSelected} Infant${this.infantsSelected !== 1 ? 's' : ''
        }`
        : '');

    const selectedDestination = this.destinations.find(
      (dest) => dest._id === bookingData?.destinationId,
    );
    const selectedHotel = this.hotelList.find(
      (hotel) => hotel._id === bookingData?.hotelId,
    );

    if (selectedDestination) {
      this.form
        .get('destination')
        ?.patchValue(selectedDestination._id, { emitEvent: false });
      this.hotelListOnSelectedDestination = this.hotelList.filter(
        (hotel) => hotel.destinationInfo?.id === selectedDestination._id,
      );
    }

    if (selectedHotel) {
      this.form
        .get('hotelItem')
        ?.patchValue(selectedHotel._id, { emitEvent: false });
    }

    if (bookingData?.checkoutUrl) {
      this.checkoutUrl = bookingData.checkoutUrl;
    }

    this.updateSearchDisabled();
    this.cdr.detectChanges();
  }

  private tryApplyBookingData(): void {
    if (this.applyBookingTries++ > this.maxApplyBookingTries) {
      console.warn(
        'Booking data still not ready after max retries — stopping.',
      );
      return;
    }

    if (
      !this.bookingDataState ||
      !this.destinations.length ||
      !this.hotelList.length
    ) {
      setTimeout(() => this.tryApplyBookingData(), 300);
      return;
    }

    this.applyBookingData(this.bookingDataState);
  }

  get previewImageCount(): number {
    return this.isMobile ? 4 : 6;
  }

  private updateGalleryPreview(categories: GalleryCategoryApi[]): void {
    const previewCount = this.previewImageCount;
    this.visibleImages = this.buildVisiblePreviewImages(categories, previewCount);
    const totalImages = this.countAllImages(categories);
    this.overflowCount = Math.max(0, totalImages - this.visibleImages.length);
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.scrollToTop();
      this.isMobile = window.innerWidth <= 768;
    }

    this.form = this.fb.group({
      hotelItem: [null],
      destination: [null],
    });

    this.hotelSlug = this.route.snapshot.paramMap.get('slug') || '';

    const navState = this.router.getCurrentNavigation()?.extras?.state as any;
    const bookingFromState =
      navState?.bookingData || (history.state as any)?.bookingData || null;

    if (bookingFromState) {
      this.bookingDataState = bookingFromState;

      this.pendingBookingHotelId = bookingFromState?.hotelId ?? null;
      this.pendingBookingDestinationId =
        bookingFromState?.destinationId ?? null;
      this.hasPendingBookingState = true;

      this.applyBookingTries = 0;
    }

    this.getDestinations();
    this.ensureHotelListLoaded();

      this.getHotelSlug();
    

    this.router.events.subscribe((event: any) => {
      if (event.constructor.name === 'NavigationEnd') {
        const latestBookingData = history.state?.bookingData;
        if (latestBookingData) {
          this.bookingDataState = latestBookingData;
          this.applyDatesFromBookingData(this.bookingDataState);
          this.pendingBookingHotelId = latestBookingData?.hotelId ?? null;
          this.pendingBookingDestinationId =
            latestBookingData?.destinationId ?? null;
          this.hasPendingBookingState = true;
          // make sure list exists, then patch
          this.ensureHotelListLoaded();
          this.tryApplyBookingData();
        }
      }
    });
    this.isMobile = window.innerWidth <= 768;

    const stateShow = history.state?.showAvailability;
    const stateReset = history.state?.resetAvailability;

    if (stateReset) {
      // coming from hotel dropdown change: start hidden
      this.showAvailability = false;
    } else if (stateShow) {
      //  coming from search flow: open automatically
      this.showAvailability = true;
    }
  }

  @HostListener('window:resize')
  onResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;

    if (wasMobile !== this.isMobile && this.hotelData?.galleryInfo) {
      this.updateGalleryPreview(this.hotelData.galleryInfo);
    }
  }

  scrollToTop() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  openInlineVideo(url?: string | null) {
    const videoUrl = url ?? this.primaryVideoUrl ?? undefined;
    if (!videoUrl) return;

    this.showVideoOverlay = true;

    setTimeout(() => {
      const el = document.getElementById(
        'hotel-inline-video',
      ) as HTMLVideoElement | null;
      if (el) {
        el.src = videoUrl;
        try {
          el.play().catch(() => { });
        } catch { }
      }
      document.body.style.overflow = 'hidden';
    }, 0);
  }

  closeInlineVideo() {
    const el = document.getElementById(
      'hotel-inline-video',
    ) as HTMLVideoElement | null;
    try {
      el?.pause();
      if (el) el.currentTime = 0;
    } catch { }
    this.showVideoOverlay = false;
    document.body.style.overflow = '';
  }

  getHotelSlug() {
    this.beginLoad();
    this.destinationsService
      .getHotelBySlug(this.hotelSlug)
      .pipe(finalize(() => this.endLoad()))
      .subscribe({
        next: (res) => {
          if (!res) {
            console.error('No hotel data received');
            return;
          }

          this.hotelData = res;
          this.hotelTitle = res.name || '';
          this.setDescriptionHtml(res?.description);

          this.applyMetaTagsFromHotel(res);
          this.setHotelDetailsJsonLd();

          if (res.galleryInfo && Array.isArray(res.galleryInfo)) {
            this.galleryTagGroups = this.buildGalleryTagGroups(res.galleryInfo);
            this.galleryPreviewGroups = this.galleryTagGroups.slice(0, 3);
            this.hasMoreGalleryGroups = this.galleryTagGroups.length > 3;
            this.moreGalleryGroupsCount = Math.max(
              0,
              this.galleryTagGroups.length - 3,
            );
            this.updateGalleryPreview(res.galleryInfo);
          }

          if (
            res.galleryInfo &&
            Array.isArray(res.galleryInfo) &&
            res.galleryInfo.length > 0
          ) {
            const firstCat = res.galleryInfo[0];
            if (
              firstCat.items &&
              Array.isArray(firstCat.items) &&
              firstCat.items.length > 0
            ) {
              const maybeVideo = firstCat.items.find(
                (item: any) => (item.fileType ?? '').toLowerCase() === 'video',
              );

              if (maybeVideo && (maybeVideo.cdnUrl || maybeVideo.location)) {
                const videoUrl = maybeVideo.cdnUrl || maybeVideo.location;
                this.primaryVideoUrl = videoUrl;
                this.primaryVideoSafeUrl =
                  this.sanitizer.bypassSecurityTrustUrl(videoUrl);
                this.hasVideoFile = true;

                if (maybeVideo.metadata?.poster) {
                  this.primaryVideoPoster = maybeVideo.metadata.poster;
                }
              }
            }
          }

          if (res.address) {
            this.hotelLocation = res.address;
          }

          const raw = res?.mapLink || '';
          const embedUrl = this.toGoogleEmbedUrl(raw);
          this.safeMapUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);

          if (Array.isArray(res?.attractions)) {
            this.attractions = res?.attractions;
          }

          // 🔥 Load hotel list after hotel data is loaded
          this.getHotelList(() => {
            this.populateFormFields(this.hotelData);
            if (this.bookingDataState) this.tryApplyBookingData();
            this.setHotelDetailsJsonLd();
            this.cdr.markForCheck();
          });
        },
        error: (err) => {
          console.error('Failed to load hotel data', err);
        },
      });
  }

  toGoogleEmbedUrl(raw: string): string {
    if (!raw) return '';

    let value = raw.trim();

    if (value.startsWith('<iframe')) {
      const srcMatch =
        value.match(/src\s*=\s*"([^"]+)"/i) ||
        value.match(/src\s*=\s*'([^']+)'/i);

      if (srcMatch?.[1]) value = srcMatch[1].trim();
    }

    try {
      const u = new URL(value);

      if (!u.hostname.includes('google.')) return value;

      if (u.pathname.includes('/maps/embed') && u.searchParams.has('pb')) {
        const pb = u.searchParams.get('pb') || '';
        const m =
          pb.match(/0x[0-9a-fA-F]+:0x([0-9a-fA-F]+)/) ||
          pb.match(/0x[0-9a-fA-F]+%3A0x([0-9a-fA-F]+)/);

        if (m?.[1]) {
          const cidDec = BigInt('0x' + m[1]).toString(10);

          const out = new URL('https://www.google.com/maps');
          out.searchParams.set('cid', cidDec);
          out.searchParams.set('output', 'embed');
          out.searchParams.set('mapclient', 'embed');
          out.searchParams.set('hl', 'en-GB');
          out.searchParams.set('gl', 'US');

          return out.toString();
        }
        return value;
      }

      if (u.pathname === '/maps' || u.pathname.startsWith('/maps')) {
        u.searchParams.set('output', 'embed');
        u.searchParams.set('mapclient', 'embed');
        return u.toString();
      }

      return value;
    } catch {
      return value;
    }
  }

  getHotelList(callback?: () => void) {
    this.beginLoad();
    this.destinationsService
      .searchHotels('', this.page, this.limit)
      .pipe(finalize(() => this.endLoad()))
      .subscribe({
        next: (res) => {
          this.hotelList = res?.results || [];
          console.log('Hotel list loaded:', this.hotelList.length);

          if (this.hotelData && this.destinations.length > 0) {
            this.populateFormFields(this.hotelData);
          }
          // priority yo booking state (if present)
          this.applyHotelSelectionFromStateIfNeeded();

          this.cdr.detectChanges();
          if (callback) callback();
        },
        error: (error) => {
          console.error('Error loading hotel list:', error);
        },
      });
  }

  private populateFormFields(hotelData: any): void {
    if (this.destinations.length === 0 || this.hotelList.length === 0) {
      return;
    }

    const destinationId = hotelData?.destination;

    const selectedDestination = this.destinations.find(
      (dest: any) => dest?._id === destinationId,
    );

    if (selectedDestination) {
      this.form
        .get('destination')
        ?.patchValue(selectedDestination._id, { emitEvent: false });

      this.hotelListOnSelectedDestination = this.hotelList.filter(
        (hotel: any) => hotel?.destinationInfo?.id === selectedDestination._id,
      );

      const hotelToSelect = this.hotelList.find(
        (h) => h.slug === this.hotelSlug,
      );

      if (hotelToSelect) {
        this.form
          .get('hotelItem')
          ?.patchValue(hotelToSelect._id, { emitEvent: false });
        this.selectedHotel = hotelToSelect._id;
      }
    }

    this.cdr.detectChanges();
  }

  getDestinations(): void {
    this.beginLoad();
    this.destinationService
      .getDestinationDropDown({ country: 'India' })
      .pipe(finalize(() => this.endLoad()))
      .subscribe({
        next: (res: any) => {
          this.destinations = res?.data || [];
          this.tryApplyBookingData();
        },
        error: (err) => {
          console.error('Error loading destinations:', err);
        },
      });
  }

  ensureHotelListLoaded(callback?: () => void) {
    if (Array.isArray(this.hotelList) && this.hotelList.length > 0) {
      this.applyHotelSelectionFromStateIfNeeded();
      callback?.();
      return;
    }
    this.getHotelList(() => {
      this.applyHotelSelectionFromStateIfNeeded();
      callback?.();
    });
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  openCalendar(event: Event) {
    event.stopPropagation();
    if (this.isDropdownVisible) {
      this.isDropdownVisible = false;
      this.removeScrollListener();
      return;
    }

    this.isAdultDropdownVisible = false;

    const target = event.currentTarget as HTMLElement;
    if (!target) return;

    this.currentDropdownTarget = target;
    this.updateCalendarDropdownPosition(target);
    this.isDropdownVisible = true;

    // Add scroll listener to update position while scrolling
    this.scrollListener = () => this.updateCalendarDropdownPosition(target);
    window.addEventListener('scroll', this.scrollListener, true);
  }

  openAdultDropdown(event: Event) {
    event.stopPropagation();

    if (this.isAdultDropdownVisible) {
      this.isAdultDropdownVisible = false;
      this.removeScrollListener();
      return;
    }

    this.isDropdownVisible = false;

    const target = event.currentTarget as HTMLElement;
    if (!target) return;

    this.currentDropdownTarget = target;
    this.updateAdultDropdownPosition(target);
    this.isAdultDropdownVisible = true;

    // Add scroll listener to update position while scrolling
    this.scrollListener = () => this.updateAdultDropdownPosition(target);
    window.addEventListener('scroll', this.scrollListener, true);
  }

  private updateCalendarDropdownPosition(target: HTMLElement) {
    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const top = rect.bottom;
    const left = rect.left;
    const dropdownHeight = 400;
    const spaceBelow = viewportHeight - rect.bottom;

    this.dropdownPosition = {
      top: `${top}px`,
      left: `${left}px`,
      maxHeight:
        spaceBelow >= dropdownHeight
          ? `${dropdownHeight}px`
          : `${spaceBelow - 20}px`,
      width: `${rect.width}px`,
      overflowY: 'auto',
    };
  }

  private updateAdultDropdownPosition(target: HTMLElement) {
    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const top = rect.bottom;
    const left = rect.left;
    const dropdownHeight = 500;
    const spaceBelow = viewportHeight - rect.bottom;

    this.dropdownPosition = {
      top: `${top}px`,
      left: `${left}px`,
      maxHeight:
        spaceBelow >= dropdownHeight
          ? `${dropdownHeight}px`
          : `${spaceBelow - 20}px`,
      width: `${rect.width}px`,
      overflowY: 'auto',
    };
  }

  private removeScrollListener() {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
    this.currentDropdownTarget = null;
  }

  // Update your existing closeAllDropdowns to clean up listener
  @HostListener('document:click', ['$event'])
  closeAllDropdowns(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const clickedInsideGuestMenu = !!target.closest('app-room-guest-menu');
    const clickedInsideDateDropdown = !!target.closest('.date-dropdown');
    const clickedInsideMatOverlay = !!target.closest('.cdk-overlay-container');

    if (
      clickedInsideGuestMenu ||
      clickedInsideDateDropdown ||
      clickedInsideMatOverlay
    )
      return;

    this.isDropdownVisible = false;
    this.isAdultDropdownVisible = false;
    this.removeScrollListener(); // ✅ Clean up listener when closing
  }

  // Clean up on destroy
  ngOnDestroy(): void {
    this.removeScrollListener(); // ✅ Clean up listener

    if (this.lgInstance) {
      try {
        this.lgInstance.destroy();
      } catch { }
    }
    this.metaTagsService.setDefault(this.getCurrentUrlSafe());
  }

  onSelectionChange(dateSelection: any) {
    this.checkIn = dateSelection.start;
    this.checkOut = dateSelection.end;

    if (this.checkIn && this.checkOut) {
      if (this.isSameDay(this.checkIn, this.checkOut)) {
        this.checkOut = null;
        this.showFormError('Check-out must be at least 1 day after check-in.');
        return;
      } else {
        this.formErrorMessage = null;
        this.isDropdownVisible = false;
      }
    }
    this.saveDates();
  }

  onHotelChange(event: any) {
  console.log('Hotel change payload:', event);

  const selectedId = typeof event === 'string' ? event : event?._id;
  const selectedHotel = this.hotelList.find((hotel) => hotel._id === selectedId);

  if (!selectedHotel?.slug) {
    console.warn('Selected hotel not found from change payload:', event);
    return;
  }

  const destSlug = this.getDestSlugFromHotel(selectedHotel);
  if (!destSlug) {
    console.warn('Destination slug missing for selected hotel:', selectedHotel);
    return;
  }

  this.saveDates();
  this.showAvailability = false;
  this.cdr.detectChanges();

  this.router.navigate(
    ['/our-hotels', destSlug, selectedHotel.slug]);
}

  // onHotelChange(event: any) {
  //   this.saveDates();
  //   this.cartStore.clear();
  //   const selectedHotel = this.hotelList.find((hotel) => hotel._id === event);

  //   if (selectedHotel?.slug) {
  //     // hide embedded availability immediately when switching hotel
  //     this.showAvailability = false;
  //     this.cdr.detectChanges();

  //     // navigate to new hotel description, and tell next page to start "clean"
  //     this.router.navigate(['/our-hotels', selectedHotel.slug], {
  //       state: { resetAvailability: true },
  //     });
  //   }
  // }

  minFor(): Date {
    return new Date();
  }

  onSearch() {
    if (!this.checkIn || !this.checkOut) {
      this.showFormError(
        'Please select check-in and check-out dates to search.',
      );
      return;
    }

    if (this.isSameDay(this.checkIn, this.checkOut)) {
      this.showFormError(
        'Check-out date must be at least 1 day after check-in.',
      );
      return;
    }

    const selectedHotel = this.hotelList.find(
      (hotel) => hotel._id === this.form.get('hotelItem')?.value,
    );

    if (!selectedHotel) {
      this.showFormError('Please select a hotel to search.');
      return;
    }

    const partnerHotelCode =
      selectedHotel?.partnerCode ||
      selectedHotel?.partnerHotelCode ||
      this.hotelData?.partnerCode ||
      this.hotelData?.partnerHotelCode;

    if (!partnerHotelCode) {
      this.showFormError('Partner hotel code missing for this hotel.');
      return;
    }

    const payload = {
      startDate: this.formatYmd(this.checkIn),
      endDate: this.formatYmd(this.checkOut),
      party: this.roomGuests.map((r) => {
        const partyItem: any = { adults: r.adults ?? 1 };

        if ((r.children ?? 0) > 0) {
          partyItem.children = (r.childAges ?? [])
            .slice(0, r.children)
            .map((a) => a ?? 9);
        }

        return partyItem;
      }),
      deviceType: window.innerWidth < 768 ? 'Mobile' : 'Desktop',
      hotels: [{ partnerHotelCode }],
    };

    const targetSlug = selectedHotel.slug || this.hotelSlug;
    if (!targetSlug) {
      this.showFormError('Hotel slug missing.');
      return;
    }

    const destSlug =
    this.getDestSlugFromHotel(selectedHotel) ||
    this.getDestSlugFromHotel(this.hotelData);

    this.formErrorMessage = null;
    this.isDropdownVisible = false;
    this.isAdultDropdownVisible = false;

    if (this.hasItemsInCart()) {
      this.pendingAvailabilityPayload = payload;
      this.pendingAvailabilityHotelSlug = targetSlug;
      this.pendingAvailabilityDestinationSlug = destSlug;
      this.openClearCartConfirm('availability');
      return;
    }

    this.bookingService.setPayload(payload);
    this.closeInlineVideo();
    this.closeGalleryModal();
    this.setBodyScrollLock(false);

    if (!destSlug) {
      this.showFormError('Destination slug missing.');
      return;
    }

    if (targetSlug !== this.hotelSlug) {
      this.router
        .navigate(['/our-hotels',destSlug, targetSlug], { replaceUrl: true })
        .then(() =>
          this.router.navigate(['/our-hotels', destSlug,targetSlug, 'availability']),
        )
        .then(() => {
          if (isPlatformBrowser(this.platformId)) {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          }
        });
      return;
    }

    this.router
      .navigate(['/our-hotels', destSlug, targetSlug, 'availability'])
      .then(() => {
        if (isPlatformBrowser(this.platformId)) {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      });
  }

  openCheckout() {
    if (!this.hotelData?.checkoutUrl) return;
    if (isPlatformBrowser(this.platformId)) {
      window.open(this.hotelData.checkoutUrl, '_blank', 'noopener noreferrer');
    }
  }

  onReadAllReviews() {
    console.log('Read all reviews clicked');
  }

  handlePreviewPlayPause(shouldPlay: boolean) {
    const videoEl = this.autoPlayVideo?.nativeElement;
    if (!videoEl) return;

    if (shouldPlay) {
      videoEl
        .play()
        .then(() => {
          this.isPreviewPlaying = true;
          this.cdr.markForCheck();
        })
        .catch(() => { });
    } else {
      videoEl.pause();
      this.isPreviewPlaying = false;
      this.cdr.markForCheck();
    }
  }

  private scrollToSearchForm() {
    if (isPlatformBrowser(this.platformId)) {
      const el =
        document.getElementById('form-error') ||
        document.getElementById('search-form');

      if (!el) return;

      const rect = el.getBoundingClientRect();
      const offset = window.scrollY + rect.top - 100;

      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }
  }

  private formatYmd(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  goToAvailabilityPage(room: any, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.checkIn || !this.checkOut) {
      this.showFormError('Please select rooms, check in and check out dates.');
      return;
    }

    if (this.isSameDay(this.checkIn, this.checkOut)) {
      this.showFormError(
        'Check-out date must be at least 1 day after check-in.',
      );
      return;
    }

    const partnerHotelCode =
      this.hotelData?.partnerCode || this.hotelData?.partnerHotelCode;

    if (!partnerHotelCode) {
      this.showFormError('Partner hotel code missing for this hotel.');
      return;
    }

    const payload = {
      startDate: this.formatYmd(this.checkIn),
      endDate: this.formatYmd(this.checkOut),
      party: this.roomGuests.map((r) => {
        const partyItem: any = { adults: r.adults ?? 1 };

        if ((r.children ?? 0) > 0) {
          partyItem.children = (r.childAges ?? [])
            .slice(0, r.children)
            .map((a) => a ?? 9);
        }

        return partyItem;
      }),
      deviceType: window.innerWidth < 768 ? 'Mobile' : 'Desktop',
      hotels: [{ partnerHotelCode }],
    };

    const destSlug =
    this.getDestSlugFromHotel(this.hotelData);

    // ✅ NEW: confirm clear cart first
    if (this.hasItemsInCart()) {
      this.pendingAvailabilityPayload = payload;
      this.pendingAvailabilityHotelSlug = this.hotelSlug;
      this.pendingAvailabilityDestinationSlug = destSlug;
      this.openClearCartConfirm('availability');
      return;
    }

    // ✅ Normal flow (unchanged)
    this.bookingService.setPayload(payload);

    this.isDropdownVisible = false;
    this.isAdultDropdownVisible = false;
    this.closeInlineVideo();
    this.closeGalleryModal();
    this.setBodyScrollLock(false);

    if (this.hotelSlug !== this.hotelSlug /* if you ever change slug here */) {
      this.router.navigate(['/our-hotels', this.hotelSlug], {
        state: { showAvailability: true },
      });
      return;
    }

    this.revealAvailabilityPanel();
  }

  private updateSearchDisabled(): void {
    if (!this.hotelData || !this.form) {
      this.searchDisabled = true;
      return;
    }

    const currentDestId = this.form.get('destination')?.value;
    const currentHotelId = this.form.get('hotelItem')?.value;

    const hotelDestId =
      this.hotelData?.destinationInfo?.id ??
      this.hotelData?.destination ??
      null;
    const hotelId = this.hotelData?._id ?? null;

    if (!currentDestId || !currentHotelId || !hotelDestId || !hotelId) {
      this.searchDisabled = true;
      return;
    }

    this.searchDisabled =
      currentDestId === hotelDestId && currentHotelId === hotelId;
  }

  private getItemUrl(item?: GalleryMediaItemApi): string {
    return item?.cdnUrl || item?.location || '';
  }

  private getItemCover(item?: GalleryMediaItemApi): string {
    return (
      item?.metadata?.thumbnail ||
      item?.metadata?.poster ||
      this.getItemUrl(item) ||
      'https://via.placeholder.com/800x600?text=No+Image'
    );
  }

  private isImage(item?: GalleryMediaItemApi): boolean {
    return (item?.fileType || '').toLowerCase() === 'image';
  }

  private buildGalleryTagGroups(
    categories: GalleryCategoryApi[],
  ): GalleryTagGroup[] {
    const safe = (categories ?? []).filter((c) => (c.items?.length ?? 0) > 0);

    const groups = safe.map((cat) => {
      const items = cat.items ?? [];
      const label = (cat.hotelTagName || 'Others').trim();

      const coverItem = items.find((i) => this.isImage(i)) ?? items[0];
      const coverUrl = this.getItemCover(coverItem);

      return {
        tagId: label,
        label,
        count: items.length,
        coverUrl,
      };
    });

    return groups.sort((a, b) => b.count - a.count);
  }

  openAllGalleryModal() {
    this.galleryModalTag = null;
    this.isGalleryModalOpen = true;
    this.setBodyScrollLock(true);
  }

  openGalleryGroupModal(group: GalleryTagGroup) {
    this.galleryModalTag = group?.tagId ?? null;
    this.isGalleryModalOpen = true;
    this.setBodyScrollLock(true);
  }

  closeGalleryModal() {
    this.isGalleryModalOpen = false;
    this.galleryModalTag = null;
    this.setBodyScrollLock(false);
  }

  private onlyImageCategories(
    categories: GalleryCategoryApi[],
  ): GalleryCategoryApi[] {
    return (categories ?? [])
      .map((cat) => ({
        ...cat,
        items: (cat.items ?? []).filter(
          (i) => this.isImage(i) && this.getItemUrl(i),
        ),
      }))
      .filter((cat) => (cat.items?.length ?? 0) > 0);
  }

  private distributeSlots(tagCount: number, totalSlots = 6): number[] {
    if (tagCount <= 0) return [];
    if (tagCount === 1) return [totalSlots];
    if (tagCount === 2) return [3, 3];
    if (tagCount >= totalSlots) return Array(totalSlots).fill(1);

    const base = Math.floor(totalSlots / tagCount);
    let rem = totalSlots % tagCount;

    const slots = Array(tagCount).fill(base);
    for (let i = 0; i < slots.length && rem > 0; i++, rem--) slots[i] += 1;
    return slots;
  }

  private buildVisiblePreviewImages(
    categories: GalleryCategoryApi[],
    totalSlots = 6,
  ): string[] {
    const ordered = this.onlyImageCategories(categories);
    if (!ordered.length || totalSlots <= 0) return [];

    const result: string[] = [];

    const takeCount = Math.min(ordered.length, totalSlots);
    for (let i = 0; i < takeCount; i++) {
      const first = (ordered[i].items ?? [])
        .map((it) => this.getItemUrl(it))
        .filter(Boolean)[0];

      if (first) result.push(first);
    }

    if (result.length >= totalSlots) return result.slice(0, totalSlots);

    const lastCat = ordered[takeCount - 1] ?? ordered[ordered.length - 1];
    const lastUrls = (lastCat.items ?? [])
      .map((it) => this.getItemUrl(it))
      .filter(Boolean);

    let idx = 1;
    while (result.length < totalSlots && idx < lastUrls.length) {
      result.push(lastUrls[idx]);
      idx++;
    }

    return result.slice(0, totalSlots);
  }

  private countAllImages(categories: GalleryCategoryApi[]): number {
    return (categories ?? [])
      .flatMap((c) => c.items ?? [])
      .filter((i) => this.isImage(i) && this.getItemUrl(i)).length;
  }

  roomGuests: RoomGuest[] = [
    { id: 'r1', adults: 2, children: 0, childAges: [] },
  ];

  onGuestApply(ev: {
    rooms: RoomGuest[];
    totals: { rooms: number; adults: number; children: number };
  }) {
    this.roomGuests = ev.rooms;

    this.roomsSelected = ev.totals.rooms;
    this.adultsSelected = ev.totals.adults;
    this.childrenSelected = ev.totals.children;

    this.dynamicPlaceholder =
      `${this.roomsSelected} Room${this.roomsSelected !== 1 ? 's' : ''} | ` +
      `${this.adultsSelected} Adult${this.adultsSelected !== 1 ? 's' : ''} | ` +
      `${this.childrenSelected} Child${this.childrenSelected !== 1 ? 'ren' : ''
      }` +
      (this.infantsSelected > 0
        ? ` | ${this.infantsSelected} Infant${this.infantsSelected !== 1 ? 's' : ''
        }`
        : '');

    this.isAdultDropdownVisible = false;
  }

  // 🔥 Helper method to generate unique IDs
  private makeId(): string {
    try {
      // @ts-ignore
      return crypto?.randomUUID?.() ?? Math.random().toString(16).slice(2);
    } catch {
      return Math.random().toString(16).slice(2);
    }
  }

  private hasItemsInCart(): boolean {
    const cart = this.cartStore.cart?.();
    const items = cart?.items ?? [];

    if (!Array.isArray(items)) return false;

    // ✅ works for qty / quantity / count and also fallback to length
    return items.some((i: any) => {
      const q = Number(
        i?.qty ?? i?.quantity ?? i?.count ?? i?.rooms ?? i?.roomQty ?? 1,
      );
      return q > 0;
    });
  }

  private hardResetCartStore() {
    this.cartStore.setCart({ items: [] } as any);
    // force UI update
    this.cdr.detectChanges();
    try {
      this.appRef.tick();
    } catch { }
  }

  private resetLocalCart() {
    const prev = this.cartStore.cart?.() ?? {};
    this.cartStore.setCart({
      ...prev,
      _id: null, // ✅ remove server cart id if present
      items: [], // ✅ empty items
      totalPricing: {
        currency: 'INR',
        subtotal: 0,
        taxAmount: 0,
        totalPayable: 0,
      },
    } as any);
  }

  private openClearCartConfirm(action: 'search' | 'availability') {
    this.pendingAction = action;
    this.showClearCartConfirm = true;
    this.setBodyScrollLock(true);
    this.cdr.detectChanges();
  }

  onClearCartCancel() {
    this.pendingAction = null;
    this.pendingSearchState = null;
    this.pendingAvailabilityPayload = null;
    this.pendingAvailabilityHotelSlug = null;
    this.pendingSearchDestinationSlug = null;

    this.showClearCartConfirm = false;
    this.setBodyScrollLock(false);
    this.cdr.detectChanges();
  }

  onClearCartOk() {
    const action = this.pendingAction;

    this.showClearCartConfirm = false;
    this.setBodyScrollLock(false);

    const searchState = this.pendingSearchState;
    const availabilityPayload = this.pendingAvailabilityPayload;
    const availabilitySlug =
      this.pendingAvailabilityHotelSlug || this.hotelSlug;

    const searchDestSlug =
      this.pendingSearchDestinationSlug ||
      this.getDestSlugFromHotel(this.hotelData);

    const availabilityDestSlug =
      this.pendingAvailabilityDestinationSlug ||
      this.getDestSlugFromHotel(this.hotelData);

    this.pendingAction = null;
    this.pendingSearchState = null;
    this.pendingAvailabilityPayload = null;
    this.pendingAvailabilityHotelSlug = null;
    this.pendingSearchDestinationSlug = null;
    this.pendingAvailabilityDestinationSlug = null;

    this.beginLoad();

    this.hardResetCartStore();

    this.cartApi
      .clearCartCookies()
      .pipe(
        switchMap(() => this.cartApi.getCart()),
        catchError(() => of({ items: [] })),
        tap((serverCart: any) => {
          const items = Array.isArray(serverCart?.items)
            ? serverCart.items
            : [];
          if (items.length === 0) {
            this.cartStore.setCart({ items: [] } as any);
          } else {
            // if you want to strictly ALWAYS clear:
            this.cartStore.setCart({ items: [] } as any);
          }

          this.cdr.detectChanges();
          try {
            this.appRef.tick();
          } catch {}
        }),
        finalize(() => this.endLoad())
      )
      .subscribe(() => {
        if (action === 'search') {
          if (searchState?.hotelSlug && searchDestSlug) {
            this.ngZone.run(() => {
              this.router
                .navigate(
                  ['/our-hotels', searchDestSlug, searchState.hotelSlug],
                  {
                    state: { bookingData: searchState.bookingData },
                  }
                )
                .then(() => {
                  if (isPlatformBrowser(this.platformId)) {
                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                    this.cartStore.clear();
                  }
                });
            });
          }
          return;
        }

        if (action === 'availability') {
          if (availabilityPayload && availabilitySlug && availabilityDestSlug) {
            this.bookingService.setPayload(availabilityPayload);

            this.isDropdownVisible = false;
            this.isAdultDropdownVisible = false;
            this.closeInlineVideo();
            this.closeGalleryModal();
            this.setBodyScrollLock(false);

            if (availabilitySlug !== this.hotelSlug) {
              this.router
                .navigate(
                  ['/our-hotels', availabilityDestSlug, availabilitySlug],
                  { replaceUrl: true }
                )
                .then(() =>
                  this.router.navigate([
                    '/our-hotels',
                    availabilityDestSlug,
                    availabilitySlug,
                    'availability',
                  ])
                )
                .then(() => {
                  if (isPlatformBrowser(this.platformId)) {
                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                  }
                });
            } else {
              this.router
                .navigate([
                  '/our-hotels',
                  availabilityDestSlug,
                  availabilitySlug,
                  'availability',
                ])
                .then(() => {
                  if (isPlatformBrowser(this.platformId)) {
                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                  }
                });
            }
          }
        }
      });
  }

  private scrollToAvailability() {
    if (!isPlatformBrowser(this.platformId)) return;

    setTimeout(() => {
      const el = document.getElementById('availability-scroll');
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const top = window.scrollY + rect.top - 90;

      window.scrollTo({ top, behavior: 'smooth' });
    }, 0);
  }

  // used to force destroy + re-create BookingStepOne (so it re-reads payload in ngOnInit)
  private revealAvailabilityPanel() {
    this.showAvailability = false;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.showAvailability = true;
      this.cdr.detectChanges();
      this.scrollToAvailabilitySection();
    }, 0);
  }

  private scrollToAvailabilitySection() {
    if (!isPlatformBrowser(this.platformId)) return;

    setTimeout(() => {
      const el = document.getElementById('availability-section');
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const top = window.scrollY + rect.top - 80;

      window.scrollTo({ top, behavior: 'smooth' });
    }, 0);
  }

  private saveDates(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      sessionStorage.setItem(
        this.DATE_STATE_KEY,
        JSON.stringify({
          checkIn: this.checkIn ? this.checkIn.toISOString() : null,
          checkOut: this.checkOut ? this.checkOut.toISOString() : null,
        }),
      );
    } catch { }
  }

  private applyDatesFromBookingData(bookingData: any) {
    const inDate = bookingData?.checkInDate
      ? new Date(bookingData.checkInDate)
      : null;
    const outDate = bookingData?.checkOutDate
      ? new Date(bookingData.checkOutDate)
      : null;

    if (inDate) this.checkIn = inDate;
    if (outDate) this.checkOut = outDate;

    this.saveDates();
    this.cdr.detectChanges();
  }

  private applyHotelSelectionFromStateIfNeeded() {
    if (!this.hasPendingBookingState) return;
    if (!this.pendingBookingHotelId) return;
    if (!Array.isArray(this.hotelList) || this.hotelList.length === 0) return;

    const exists = this.hotelList.some(
      (h) => h?._id === this.pendingBookingHotelId,
    );
    if (!exists) return;
    this.form
      .get('hotelItem')
      ?.setValue(this.pendingBookingHotelId, { emitEvent: false });
    if (this.pendingBookingDestinationId) {
      this.form
        .get('destination')
        ?.setValue(this.pendingBookingDestinationId, { emitEvent: false });
    }
    this.tryApplyBookingData();
    this.hasPendingBookingState = false;
  }

  setDescriptionHtml(desc?: string | null) {
    const raw = (desc ?? '').toString();
    const decoded = isPlatformBrowser(this.platformId)
      ? this.decodeHtmlEntities(raw)
      : raw;

    this.hotelDescriptionHtml = this.cleanDescriptionHtml(decoded);
  }

  private cleanDescriptionHtml(input: string): string {
    return input
      .replace(/&nbsp;/gi, ' ')
      .replace(/<p>\s*(<br\s*\/?>|&nbsp;|\s)*<\/p>/gi, '')
      .replace(/<div>\s*(<br\s*\/?>|&nbsp;|\s)*<\/div>/gi, '')
      .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
      .trim();
  }

  private decodeHtmlEntities(input: string): string {
    const txt = document.createElement('textarea');
    txt.innerHTML = input;
    return txt.value;
  }

  private getDestSlugFromHotel(hotel: any): string {
  const rawSlug =
    hotel?.destinationInfo?.slug ||
    hotel?.cityInfo?.slug ||
    '';

  if (rawSlug) return String(rawSlug).toLowerCase().trim();

  const rawName =
    hotel?.destinationInfo?.name ||
    hotel?.cityInfo?.name ||
    this.hotelData?.destinationInfo?.name ||
    this.hotelData?.cityInfo?.name ||
    '';

  return String(rawName)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

  private getCurrentUrlSafe(): string {
    const routeUrl = this.router.url || '';
    return isPlatformBrowser(this.platformId) ? window.location.href : routeUrl;
  }

  private getHotelPageUrl(): string {
    const destSlug = this.getDestSlugFromHotel(this.hotelData);
    const hotelSlug = this.hotelData?.slug || this.hotelSlug || '';

    if (destSlug && hotelSlug) {
      return `${this.SITE_URL}/our-hotels/${encodeURIComponent(destSlug)}/${encodeURIComponent(hotelSlug)}`;
    }

    if (hotelSlug) {
      return `${this.SITE_URL}/our-hotels/${encodeURIComponent(hotelSlug)}`;
    }

    return this.getCurrentUrlSafe();
  }

  private applyMetaTagsFromHotel(hotel: any): void {
    const url = this.getCurrentUrlSafe();

    const title =
      hotel?.seo?.ogTitle?.trim() ||
      hotel?.seo?.title?.trim() ||
      `${hotel?.name || 'Hotel'} | TripXL Hotels`;

    const description =
      hotel?.seo?.ogDescription?.trim() ||
      hotel?.seo?.description?.trim() ||
      `Explore ${hotel?.name || 'this hotel'} with TripXL. Browse rooms, amenities, gallery and availability.`;

    const image =
      hotel?.seo?.ogImageUrl ||
      hotel?.seo?.image ||
      hotel?.coverImage ||
      hotel?.mediaInfos?.[0]?.cdnUrl ||
      hotel?.thumbnail ||
      '';

    this.metaTagsService.setMetaTags(title, url, description, image);
  }

  private setHotelDetailsJsonLd(): void {
    console.log('Setting JSON-LD schema for hotel details page', this.hotelData);
    if (!this.hotelData) return;

    const pageUrl = this.getHotelPageUrl();
    const hotelName = this.hotelData?.name || 'Hotel';

    const image =
      this.hotelData?.seo?.ogImageUrl ||
      this.hotelData?.coverImage ||
      this.hotelData?.mediaInfos?.[0]?.cdnUrl ||
      this.hotelData?.thumbnail ||
      '';

    const city =
      this.hotelData?.cityInfo?.name ||
      this.hotelData?.destinationInfo?.name ||
      '';

    const country =
      this.hotelData?.countryInfo?.name ||
      'India';

    const address =
      this.hotelData?.address || '';

    const destName =
      this.hotelData?.destinationInfo?.name ||
      this.hotelData?.cityInfo?.name ||
      'Destination';

    const destSlug = this.getDestSlugFromHotel(this.hotelData);

    const destinationUrl = destSlug
      ? `${this.SITE_URL}/our-hotels/${encodeURIComponent(destSlug)}`
      : `${this.SITE_URL}/our-hotels`;

    const description =
      this.hotelData?.seo?.ogDescription ||
      this.hotelData?.seo?.description ||
      `Explore ${hotelName} with TripXL. Browse rooms, amenities, gallery and availability.`;

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${pageUrl}#webpage`,
          url: pageUrl,
          name: `${hotelName} | TripXL Hotels`,
          description,
          isPartOf: {
            '@id': `${this.SITE_URL}/#website`,
          },
        },
        {
          '@type': 'Hotel',
          '@id': `${pageUrl}#hotel`,
          name: hotelName,
          url: pageUrl,
          image: image ? [image] : undefined,
          description,
          address: {
            '@type': 'PostalAddress',
            streetAddress: address || undefined,
            addressLocality: city || undefined,
            addressCountry: country || undefined,
          },
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${pageUrl}#breadcrumbs`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${this.SITE_URL}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Hotels',
              item: `${this.SITE_URL}/our-hotels/all`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: destName,
              item: destinationUrl,
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: hotelName,
              item: pageUrl,
            },
          ],
        },
      ],
    };

    const jsonLdId = `hotel-detail-${this.hotelData?.slug || this.hotelSlug}`;
    this.jsonLd.setJsonLd(jsonLdId, schema);
  }
}
